import React, { useState } from "react";
import { AnalyticsFilters } from "./types/analytics.types";
import { DateRangeBar } from "./design/DateRangeBar";
import { OverviewTab } from "./components/OverviewTab";
import { GrowthTab } from "./components/GrowthTab";
import { CostsTab } from "./components/CostsTab";
import { EngagementTab } from "./components/EngagementTab";
import { LearningTab } from "./components/LearningTab";
import { ContentTab } from "./components/ContentTab";
import { NotificationsTab } from "./components/NotificationsTab";
import { UsersTab } from "./components/UsersTab";
import { AdsTab } from "./components/AdsTab";
import { SkipsTab } from "./components/SkipsTab";
import { DataQualityTab } from "./components/DataQualityTab";

import "./design/tokens.css";
import "./design/ui/ui.css";
import "./design/dashboard.css";
import "./design/legacy-compat.css";

type TabType =
  | "overview"
  | "growth"
  | "engagement"
  | "users"
  | "learning"
  | "content"
  | "skips"
  | "data-quality"
  | "ads"
  | "costs"
  | "notifications";

interface TabDef {
  id: TabType;
  label: string;
}

// Tabs grouped by the question they answer, so the nav reads as a story:
// who's here → what they do → what we teach → what it costs.
const TAB_GROUPS: { group: string; tabs: TabDef[] }[] = [
  {
    group: "Business",
    tabs: [
      { id: "overview", label: "Overview" },
      { id: "growth", label: "Growth" },
    ],
  },
  {
    group: "Engagement",
    tabs: [
      { id: "engagement", label: "Engagement" },
      { id: "users", label: "Users" },
    ],
  },
  {
    group: "Learning",
    tabs: [
      { id: "learning", label: "Learning" },
      { id: "content", label: "Content" },
      { id: "skips", label: "Skips" },
      { id: "data-quality", label: "Data Quality" },
    ],
  },
  {
    group: "Ops & Cost",
    tabs: [
      { id: "ads", label: "Ads" },
      { id: "costs", label: "LLM Cost" },
      { id: "notifications", label: "Notifications" },
    ],
  },
];

const AnalyticsDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>("overview");
  const [filters, setFilters] = useState<AnalyticsFilters>({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    endDate: new Date(),
    granularity: "daily",
  });

  // Bump to force child tabs to refetch (they own their own hooks).
  const [refreshKey, setRefreshKey] = useState(0);
  const handleRefresh = () => setRefreshKey((k) => k + 1);

  const renderTab = () => {
    switch (activeTab) {
      case "overview":
        return <OverviewTab filters={filters} />;
      case "growth":
        return <GrowthTab filters={filters} />;
      case "costs":
        return <CostsTab filters={filters} />;
      case "engagement":
        return <EngagementTab filters={filters} onFiltersChange={setFilters} />;
      case "learning":
        return <LearningTab filters={filters} onFiltersChange={setFilters} />;
      case "content":
        return <ContentTab filters={filters} onFiltersChange={setFilters} />;
      case "data-quality":
        return <DataQualityTab filters={filters} onFiltersChange={setFilters} />;
      case "notifications":
        return <NotificationsTab filters={filters} onFiltersChange={setFilters} />;
      case "users":
        return <UsersTab filters={filters} />;
      case "ads":
        return <AdsTab filters={filters} />;
      case "skips":
        return <SkipsTab filters={filters} />;
      default:
        return null;
    }
  };

  return (
    <div className="an-root">
      <div className="an-shell">
        <div className="an-header">
          <div>
            <h1 className="an-header-title">Analytics</h1>
            <p className="an-header-sub">
              Product, engagement, learning, and cost metrics for Chatterbox
            </p>
          </div>
          <DateRangeBar filters={filters} onChange={setFilters} onRefresh={handleRefresh} />
        </div>

        <nav className="an-tabs">
          {TAB_GROUPS.map((group) => (
            <div key={group.group} className="an-tab-group">
              {group.tabs.map((tab) => (
                <button
                  key={tab.id}
                  className={`an-tab ${activeTab === tab.id ? "an-tab--active" : ""}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          ))}
        </nav>

        <div className="an-body" key={`${activeTab}-${refreshKey}`}>
          {renderTab()}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
